<template>
  <admin-layout :title="$t('create_x', { x: $t('Sale') })">
    <tec-form-section @submitted="submit" :fullWidth="true">
      <template #title>{{ $t('create_x', { x: $t('Sale') }) }}</template>
      <template #description>{{ $t('Please fill the form below to add new record.') }}</template>

      <template #form>
        <div class="max-w-full col-span-full flex flex-col md:grid sm:grid-cols-2 gap-6">
          <div>
            <text-input
              v-model="form.number"
              :error="$page.props.errors.number ? $page.props.errors.number.replace('{attribute}', 'invoice number') : null"
              :label="$t('Invoice Number')"
            />
          </div>
          <div>
            <text-input
              v-model="form.locator_number"
              :error="$page.props.errors.locator_number ? $page.props.errors.locator_number.replace('{attribute}', 'locator number') : null"
              :label="$t('Locator Number')"
            />
          </div>
          <div>
            <text-input
              type="date"
              v-model="form.date"
              format="yyyy-mm-dd"
              :error="$page.props.errors.date ? $page.props.errors.date.replace('{attribute}', 'date') : null"
              :label="$t('Date')"
            />
          </div>
          <div>
            <div class="col-span-6 sm:col-span-4 lg:col-span-4">
              <v-select-input :label="$t('Customer')" :action="showCustomerModal" :action-text="$t('Add Customer')">
                <v-select v-model="form.customer_id" :options="customers" :reduce="customer => customer.id" label="name">
                  <template #open-indicator="{ attributes }">
                    <span v-bind="attributes">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fill-rule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </span>
                  </template>
                </v-select>
              </v-select-input>
            </div>
          </div>
          <div class="col-span-full">
            <div class="-mx-4 pb-2 overflow-x-auto min-w-full sm:-mx-6 lg:-mx-8">
              <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                <div class="overflow-hidden ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th scope="col" class="py-3.5 pl-4 pr-3 text-center text-sm font-semibold text-gray-900 sm:pl-6 w-12">No.</th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $t('Description') }}</th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">{{ $t('Quantity') }}</th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">{{ $t('Tax') }}</th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">{{ $t('HST') }}</th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">
                          {{ $t('Agent Commission') }}
                        </th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">{{ $t('Commission') }}</th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">
                          {{ $t('HST on Comm.') }}
                        </th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">{{ $t('Airfare') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) of form.items" :key="'i_' + index" class="odd:bg-white even:bg-gray-50">
                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-center text-sm font-medium text-gray-900 sm:pl-6 w-12">
                          {{ index + 1 }}
                        </td>
                        <td class="whitespace-nowrap px-3 py-2 text-sm min-w-[250px]">
                          <input
                            type="text"
                            v-model="item.description"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.description'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.description']
                                ? $page.props.errors['items.' + index + '.description'].replace('{attribute}', 'description')
                                : null
                            }}
                          </div>
                        </td>
                        <td class="whitespace-nowrap px-3 py-2 text-sm w-40 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.quantity"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.quantity'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.quantity']
                                ? $page.props.errors['items.' + index + '.quantity'].replace('{attribute}', 'quantity')
                                : null
                            }}
                          </div>
                        </td>
                        <td class="whitespace-nowrap px-3 py-2 text-sm w-40 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.tax"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.tax'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.tax']
                                ? $page.props.errors['items.' + index + '.tax'].replace('{attribute}', 'tax')
                                : null
                            }}
                          </div>
                        </td>
                        <!-- <td class="whitespace-nowrap px-3 py-2 text-sm w-40 min-w-[150px]">
                          <select
                            v-model="item.tax_rate_id"
                            class="w-full focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.tax_rate_id'] ? 'border-red-500' : 'border-gray-200'"
                          >
                            <option value=""></option>
                            <option v-for="tax_rate of tax_rates" :value="tax_rate.id" :key="'t_' + tax_rate.id">
                              {{ tax_rate.name }}
                            </option>
                          </select>
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.tax_rate_id']
                                ? $page.props.errors['items.' + index + '.tax_rate_id'].replace(
                                    '{attribute}',
                                    'tax_rate_id'
                                  )
                                : null
                            }}
                          </div>
                        </td> -->
                        <td class="whitespace-nowrap px-3 py-2 text-sm w-40 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.hst"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.hst'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.hst']
                                ? $page.props.errors['items.' + index + '.hst'].replace('{attribute}', 'hst')
                                : null
                            }}
                          </div>
                        </td>
                        <td class="relative whitespace-nowrap py-2 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 w-40 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.agent_commission"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.agent_commission'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.agent_commission']
                                ? $page.props.errors['items.' + index + '.agent_commission'].replace('{attribute}', 'agent commission')
                                : null
                            }}
                          </div>
                        </td>
                        <td class="relative whitespace-nowrap py-2 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 w-40 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.commission"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.commission'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.commission']
                                ? $page.props.errors['items.' + index + '.commission'].replace('{attribute}', 'commission')
                                : null
                            }}
                          </div>
                        </td>
                        <td class="relative whitespace-nowrap py-2 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 w-40 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.hst_on_commission"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.hst_on_commission'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.hst_on_commission']
                                ? $page.props.errors['items.' + index + '.hst_on_commission'].replace('{attribute}', 'hst_on_commission')
                                : null
                            }}
                          </div>
                        </td>
                        <td class="relative whitespace-nowrap py-2 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 w-40 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.airfare"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.airfare'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.airfare']
                                ? $page.props.errors['items.' + index + '.airfare'].replace('{attribute}', 'airfare')
                                : null
                            }}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot class="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          class="pb-1 pt-3.5 border-b pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                          colspan="2"
                        >
                          <!-- {{ $t('Total') }} -->
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold text-gray-900 w-40">
                          {{ $number(form.items.reduce((a, i) => (i.quantity ? a + Number(i.quantity) : a), 0)) }}
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold text-gray-900 w-40">
                          <div class="px-4">
                            {{ $number(form.items.reduce((a, i) => (i.tax ? a + Number(i.quantity) * Number(i.tax) : a), 0)) }}
                          </div>
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold text-gray-900 w-40">
                          <div class="px-4">
                            {{ $number(form.items.reduce((a, i) => (i.hst ? a + Number(i.quantity) * Number(i.hst) : a), 0)) }}
                          </div>
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold text-gray-900 w-40">
                          <div class="px-4">
                            {{
                              $number(
                                form.items.reduce(
                                  (a, i) => (i.agent_commission ? a + Number(i.quantity) * Number(i.agent_commission) : a),
                                  0
                                )
                              )
                            }}
                          </div>
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold text-gray-900 w-40">
                          <div class="px-4">
                            {{
                              $number(form.items.reduce((a, i) => (i.commission ? a + Number(i.quantity) * Number(i.commission) : a), 0))
                            }}
                          </div>
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold text-gray-900 w-40">
                          <div class="px-4">
                            {{
                              $number(
                                form.items.reduce(
                                  (a, i) => (i.hst_on_commission ? a + Number(i.quantity) * Number(i.hst_on_commission) : a),
                                  0
                                )
                              )
                            }}
                          </div>
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold sm:pr-6 text-gray-900 w-48">
                          <div class="px-4">
                            {{ $number(form.items.reduce((a, i) => (i.airfare ? a + Number(i.quantity) * Number(i.airfare) : a), 0)) }}
                          </div>
                        </th>
                      </tr>
                      <tr>
                        <th scope="col" class="py-1 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" colspan="2">
                          {{ $t('Total Airfare') }}
                        </th>
                        <th scope="col" class="px-3 py-1 text-left text-sm font-semibold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-48"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold sm:pr-6 text-gray-900 w-40">
                          <div class="px-4">
                            {{
                              $number(
                                form.items.reduce((a, i) => (i.airfare ? a + Number(i.quantity) * Number(i.airfare) : a), 0) +
                                  form.items.reduce((a, i) => (i.commission ? a + Number(i.quantity) * Number(i.commission) : a), 0) +
                                  form.items.reduce(
                                    (a, i) => (i.agent_commission ? a + Number(i.quantity) * Number(i.agent_commission) : a),
                                    0
                                  )
                              )
                            }}
                          </div>
                        </th>
                      </tr>
                      <tr>
                        <th scope="col" class="py-1 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" colspan="2">
                          {{ $t('Total Tax') }}
                        </th>
                        <th scope="col" class="px-3 py-1 text-left text-sm font-semibold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-48"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold sm:pr-6 text-gray-900 w-40">
                          <div class="px-4">
                            {{
                              $number(
                                form.items.reduce((a, i) => (i.tax ? a + Number(i.quantity) * Number(i.tax) : a), 0) +
                                  form.items.reduce((a, i) => (i.hst ? a + Number(i.quantity) * Number(i.hst) : a), 0) +
                                  form.items.reduce(
                                    (a, i) => (i.hst_on_commission ? a + Number(i.quantity) * Number(i.hst_on_commission) : a),
                                    0
                                  )
                              )
                            }}
                          </div>
                        </th>
                      </tr>
                      <tr>
                        <th scope="col" class="border-t text-lg py-3.5 pl-4 pr-3 text-left font-semibold text-gray-900 sm:pl-6" colspan="2">
                          {{ $t('Grand Total') }}
                        </th>
                        <th scope="col" class="px-3 border-t py-3.5 text-left text-sm font-semibold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 border-t py-3.5 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 border-t py-3.5 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 border-t py-3.5 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 border-t py-3.5 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 border-t py-3.5 text-right text-sm font-bold text-gray-900 w-48"></th>
                        <th scope="col" class="px-3 border-t py-3.5 text-right text-sm font-bold sm:pr-6 text-gray-900 w-40">
                          <div class="px-4 text-lg">
                            {{
                              $number(
                                form.items.reduce((a, i) => (i.tax ? a + Number(i.quantity) * Number(i.tax) : a), 0) +
                                  form.items.reduce((a, i) => (i.hst ? a + Number(i.quantity) * Number(i.hst) : a), 0) +
                                  form.items.reduce(
                                    (a, i) => (i.hst_on_commission ? a + Number(i.quantity) * Number(i.hst_on_commission) : a),
                                    0
                                  ) +
                                  form.items.reduce((a, i) => (i.airfare ? a + Number(i.quantity) * Number(i.airfare) : a), 0) +
                                  form.items.reduce((a, i) => (i.commission ? a + Number(i.quantity) * Number(i.commission) : a), 0) +
                                  form.items.reduce(
                                    (a, i) => (i.agent_commission ? a + Number(i.quantity) * Number(i.agent_commission) : a),
                                    0
                                  )
                              )
                            }}
                          </div>
                        </th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
            <span class="relative z-0 inline-flex rounded-md">
              <button
                type="button"
                @click="addRow()"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-200 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <span class="sr-only">Add new row</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>
              <button
                type="button"
                @click="removeRow()"
                class="-ml-px relative inline-flex items-center px-2 py-1 rounded-r-md border border-gray-200 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <span class="sr-only">Remove last row</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M18 12H6" />
                </svg>
              </button>
            </span>
          </div>
          <div>
            <div class="col-span-6 sm:col-span-4 lg:col-span-4">
              <select-input
                v-model="form.status"
                :error="$page.props.errors.status ? $page.props.errors.status.replace('{attribute}', $t('status')) : null"
                :label="$t('Status')"
              >
                <option value="Pending">{{ $t('Pending') }}</option>
                <option value="Paid">{{ $t('Paid') }}</option>
                <option value="Overdue">{{ $t('Overdue') }}</option>
                <option value="Canceled">{{ $t('Canceled') }}</option>
              </select-input>
            </div>
          </div>
          <div>
            <div class="col-span-6 sm:col-span-4 lg:col-span-4">
              <select-input
                v-model="form.payment_method"
                :error="
                  $page.props.errors.payment_method ? $page.props.errors.payment_method.replace('{attribute}', $t('payment method')) : null
                "
                :label="$t('Payment Method')"
              >
                <option value=""></option>
                <option value="Credit Card">{{ $t('Credit Card') }}</option>
                <option value="Cash">{{ $t('Cash') }}</option>
                <option value="Debit Card">{{ $t('Debit Card') }}</option>
                <option value="Transfer">{{ $t('Transfer') }}</option>
              </select-input>
            </div>
          </div>
          <div v-if="form.payment_method == 'Credit Card'">
            <div class="col-span-6 sm:col-span-4 lg:col-span-4">
              <text-input
                v-model="form.cc_fees"
                :error="$page.props.errors.cc_fees ? $page.props.errors.cc_fees.replace('{attribute}', 'credit card fee') : null"
                :label="$t('Credit Card Fee')"
              />
            </div>
          </div>

          <div v-if="$super">
            <div class="col-span-6 sm:col-span-4 lg:col-span-4">
              <select-input
                :label="$t('Agent')"
                v-model="form.agent_id"
                :error="$page.props.errors.agent_id ? $page.props.errors.agent_id.replace('{attribute}', $t('agent')) : null"
              >
                <option value=""></option>
                <option v-for="agent in users" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
              </select-input>
            </div>
          </div>

          <div class="col-span-full">
            <label for="comment" class="block text-sm font-medium text-gray-700">{{ $t('Comment') }}</label>
            <div class="mt-1">
              <textarea
                rows="4"
                id="comment"
                v-model="form.comment"
                class="shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 block w-full sm:text-sm border-gray-300 rounded-md"
              ></textarea>
            </div>
          </div>
          <div class="col-span-full">
            <div class="flex items-center">
              <button
                type="button"
                @click="form.travel_insurance = !form.travel_insurance"
                :class="form.travel_insurance ? 'bg-indigo-600' : 'bg-gray-200'"
                class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                role="switch"
                aria-checked="false"
                aria-labelledby="annual-billing-label"
              >
                <span
                  aria-hidden="true"
                  :class="form.travel_insurance ? 'translate-x-5' : 'translate-x-0'"
                  class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                ></span>
              </button>
              <span class="ml-3 cursor-pointer" id="annual-billing-label" @click="form.travel_insurance = !form.travel_insurance">
                <span class="text-sm font-medium text-gray-900">Travel Insurance</span>
              </span>
            </div>
          </div>
          <div class="col-span-full">
            <div class="flex items-center">
              <button
                type="button"
                @click="form.medical_insurance = !form.medical_insurance"
                :class="form.medical_insurance ? 'bg-indigo-600' : 'bg-gray-200'"
                class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                role="switch"
                aria-checked="false"
                aria-labelledby="annual-billing-label"
              >
                <span
                  aria-hidden="true"
                  :class="form.medical_insurance ? 'translate-x-5' : 'translate-x-0'"
                  class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                ></span>
              </button>
              <span class="ml-3 cursor-pointer" id="annual-billing-label" @click="form.medical_insurance = !form.medical_insurance">
                <span class="text-sm font-medium text-gray-900">Medical Insurance</span>
              </span>
            </div>
          </div>
        </div>
      </template>

      <template #actions>
        <tec-action-message :on="form.recentlySuccessful" class="mr-3">{{ $t('Saved.') }}</tec-action-message>
        <loading-button :loading="form.processing" :disabled="form.processing">{{ $t('Save') }}</loading-button>
      </template>
    </tec-form-section>

    <modal :show="customerModal" max-width="2xl" :closeable="false">
      <div class="px-6 py-4 print:px-0">
        <div class="flex items-center justify-between print:hidden">
          <div class="text-lg w-full flex items-center justify-between">
            {{ $t('Add New Customer') }}
          </div>
          <div class="-mr-2 flex items-center">
            <button
              @click="hideCustomerModal()"
              class="flex items-center justify-center h-8 w-8 rounded-full text-gray-600 hover:text-gray-800 hover:bg-gray-300 focus:outline-none"
            >
              <icons name="cross" class="h-5 w-5" />
            </button>
          </div>
        </div>

        <div class="mt-4 print-mt-0">
          <div class="col-span-full flex items-start flex-wrap -mr-4 md:-mr-6 md:rounded-md">
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <text-input
                  :label="$t('Name')"
                  v-model="customer_form.name"
                  :error="customer_errors.name ? customer_errors.name.replace('{attribute}', 'name') : null"
                />
              </div>
            </div>
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <text-input
                  :label="$t('Company Name')"
                  v-model="customer_form.company_name"
                  :error="customer_errors.company_name ? customer_errors.company_name.replace('{attribute}', 'company name') : null"
                />
              </div>
            </div>
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <text-input
                  :label="$t('Phone')"
                  v-model="customer_form.phone"
                  :error="customer_errors.phone ? customer_errors.phone.replace('{attribute}', 'phone').split('when')[0] : null"
                />
              </div>
            </div>
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <text-input
                  type="email"
                  :label="$t('Email')"
                  v-model="customer_form.email"
                  :error="customer_errors.email ? customer_errors.email.replace('{attribute}', 'email') : null"
                />
              </div>
            </div>

            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <text-input
                  type="date"
                  format="yyyy-mm-dd"
                  v-model="customer_form.dob"
                  :label="$t('Date of Birth')"
                  :error="customer_errors.dob ? customer_errors.dob.replace('{attribute}', 'date of birth') : null"
                />
              </div>
            </div>
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <text-input
                  :label="$t('GST')"
                  v-model="customer_form.gst"
                  :error="customer_errors.gst ? customer_errors.gst.replace('{attribute}', 'GST') : null"
                />
              </div>
            </div>
            <div class="w-full">
              <div class="mr-6 mb-6">
                <text-input
                  :label="$t('Address')"
                  v-model="customer_form.address"
                  :error="customer_errors.address ? customer_errors.address.replace('{attribute}', 'address') : null"
                />
              </div>
            </div>
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <text-input
                  :label="$t('City')"
                  v-model="customer_form.city"
                  :error="customer_errors.city ? customer_errors.city.replace('{attribute}', 'city') : null"
                />
              </div>
            </div>
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <text-input
                  :label="$t('State')"
                  v-model="customer_form.state"
                  :error="customer_errors.state ? customer_errors.state.replace('{attribute}', 'state') : null"
                />
              </div>
            </div>
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <text-input
                  :label="$t('Postal Code')"
                  v-model="customer_form.postal_code"
                  :error="customer_errors.postal_code ? customer_errors.postal_code.replace('{attribute}', 'postal code') : null"
                />
              </div>
            </div>
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <text-input
                  :label="$t('Country')"
                  v-model="customer_form.country"
                  :error="customer_errors.country ? customer_errors.country.replace('{attribute}', 'email') : null"
                />
              </div>
            </div>
          </div>
          <loading-button :loading="form.processing" :disabled="form.processing" @click="addCustomer">{{ $t('Save') }}</loading-button>
        </div>
      </div>
    </modal>
  </admin-layout>
</template>

<script>
import Modal from '@/Jetstream/Modal.vue';
import TextInput from '@/Shared/TextInput';
import SelectInput from '@/Shared/SelectInput';
import AdminLayout from '@/Layouts/AdminLayout';
import VSelectInput from '@/Shared/VSelectInput';
import LoadingButton from '@/Shared/LoadingButton';
import TecFormSection from '@/Jetstream/FormSection';
import TecActionMessage from '@/Jetstream/ActionMessage';
import TecSecondaryButton from '@/Jetstream/SecondaryButton';
import axios from 'axios';

export default {
  props: ['customers', 'tax_rates', 'next_id', 'users'],

  components: {
    Modal,
    TextInput,
    AdminLayout,
    SelectInput,
    VSelectInput,
    LoadingButton,
    TecFormSection,
    TecActionMessage,
    TecSecondaryButton,
  },

  data() {
    return {
      customerModal: false,
      customer_errors: {},
      customer_form: this.$inertia.form({
        _method: 'POST',
        name: null,
        email: null,
        phone: null,
        company: null,
        gst: null,
        vat: null,
        address: null,
        city: null,
        state: null,
        postal_code: null,
        country: null,
        dob: null,
      }),
      form: this.$inertia.form({
        _method: 'POST',
        number: this.next_id || 1,
        locator_number: null,
        date: new Date().toJSON().split('T').shift(),
        customer_id: this.$page.url.includes('customer_id=') ? this.$page.url.split('customer_id=').pop() : null,
        payment_method: null,
        cc_fees: null,
        comment: null,
        // comment: this.$page.props.settings?.default_sale_comment,
        agent_id: null,
        status: 'Pending',
        items: [
          {
            quantity: null,
            hst: null,
            description: null,
            tax_rate_id: null,
            tax: null,
            agent_commission: null,
            commission: null,
            hst_on_commission: null,
            airfare: null,
          },
          {
            quantity: null,
            hst: null,
            description: null,
            tax_rate_id: null,
            tax: null,
            agent_commission: null,
            commission: null,
            hst_on_commission: null,
            airfare: null,
          },
          {
            quantity: null,
            hst: null,
            description: null,
            tax_rate_id: null,
            tax: null,
            agent_commission: null,
            commission: null,
            hst_on_commission: null,
            airfare: null,
          },
          {
            quantity: null,
            hst: null,
            description: null,
            tax_rate_id: null,
            tax: null,
            agent_commission: null,
            commission: null,
            hst_on_commission: null,
            airfare: null,
          },
          {
            quantity: null,
            hst: null,
            description: null,
            tax_rate_id: null,
            tax: null,
            agent_commission: null,
            commission: null,
            hst_on_commission: null,
            airfare: null,
          },
        ],
        travel_insurance: false,
        medical_insurance: false,
      }),
    };
  },

  mounted() {
    let s = this.$page.url.split('customer_id=');
    if (s[1]) {
      this.$nextTick(() => (this.form.customer_id = s[1]));
    }
    // this.form.customer_id = s[1] || null;
    // console.log(this.form.date, this.form.customer_id);
  },

  methods: {
    submit() {
      this.form.post(route('sales.store'), { preserveScroll: true });
    },
    addRow() {
      this.form.items.push({
        quantity: null,
        hst: null,
        description: null,
        tax_rate_id: null,
        tax: null,
        agent_commission: null,
        commission: null,
        hst_on_commission: null,
        airfare: null,
      });
    },
    removeRow() {
      if (this.form.items.length > 5) {
        this.form.items.pop();
      }
    },
    showCustomerModal() {
      this.customerModal = true;
    },
    hideCustomerModal() {
      this.customerModal = false;
      this.customer_form.reset();
      this.customer_errors = {};
    },
    addCustomer() {
      console.log('addCustomer');
      this.customer_form.post(route('customers.store') + '?json=yes', {
        preserveScroll: true,
        onSuccess: page => {
          let s = page.url.split('customer_id=');
          if (s[1]) {
            this.customerModal = false;
            this.customer_form.reset();
            this.form.customer_id = Number(s[1]);
          }
          console.log(s[1], this.customerModal, this.form.customer_id);
        },
        onError: errors => (this.customer_errors = errors),
      });
    },
  },
};
</script>
